import { style, globalStyle, styleVariants } from '@vanilla-extract/css';
import { recipe } from '@vanilla-extract/recipes';
import { vars } from '../../theme/contract.css';
import { mobile, reducedMotion } from '../../theme/media';

// ============================================
// TYPES
// ============================================

export type InputVariant = 'default' | 'error' | 'success';
export type InputSize = 'xs' | 'sm' | 'md' | 'lg';

/**
 * 표면 축 — `glass` 가 기본(liquid glass)이고 `flat` 은 운영자 콘솔(HR · Finance · CEO · SysAdmin)
 * 절제 모티프다. 평면 배경 · hairline, backdropFilter · glow · 라벨 그라디언트 밑줄 없음.
 * 값은 Select `flat` 과 같은 토큰을 쓴다(DESIGN.md §Atom Standards 1-1).
 *
 * `variant` 에 합치지 않는 이유는 그쪽이 이미 `default | error | success` 라는 **검증 상태 축**이기
 * 때문이다. 한 enum 에 두 축을 섞으면 "flat 인데 error" 가 갈 곳을 잃는다 — §1-2 가 Button 톤 축을
 * 표면과 직교로 뺀 것과 같은 이유다. radius 는 절제의 표식이 아니라 위계 축이라 base 를 상속한다.
 */
export type InputSurface = 'glass' | 'flat';

// ============================================
// CONTAINER & LABEL
// ============================================

export const container = style({
  display: 'inline-flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.sm,
});

export const containerFullWidth = style({
  width: '100%',
});

/*
 * 밑줄이 **라벨** 을 긋는다. `labelUnderline` 은 `width: 100%` 이고 라벨은 flex column 의
 * 자식이라, 이 두 줄이 없으면 기본값 `stretch` 로 필드 폭까지 늘어난다 — 장식의 무게가
 * 컨트롤이 아니라 레이아웃의 함수가 되고, 넓은 필드에서는 제목 아래 구분선처럼 읽힌다
 * (관계 화면 툴바 694px 실측, PR #2332). `paddingRight` 는 그라디언트의 투명 꼬리 자리다.
 */
export const label = style({
  fontFamily: vars.typography.fontFamily.primary,
  fontSize: vars.typography.fontSize.sm,
  fontWeight: vars.typography.fontWeight.semibold,
  color: vars.bright.primary.light,
  letterSpacing: vars.typography.letterSpacing.wide,
  position: 'relative',
  alignSelf: 'flex-start',
  paddingBottom: '4px',
  paddingRight: '14px',
});

export const labelUnderline = style({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  height: '1px',
  background: `linear-gradient(90deg, ${vars.bright.primary.default} 0%, ${vars.bright.secondary.default} 50%, transparent 100%)`,
});

// ============================================
// WRAPPER
// ============================================

export const wrapper = style({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
});

export const wrapperFullWidth = style({
  width: '100%',
});

// ============================================
// INPUT BASE & RECIPE
// ============================================

const inputBase = {
  fontFamily: vars.typography.fontFamily.primary,
  fontWeight: vars.typography.fontWeight.regular,
  color: vars.typography.textColor.primary,
  letterSpacing: vars.typography.letterSpacing.wide,
  background: vars.form.fieldGradient,
  border: `1px solid ${vars.surface.border.primary}`,
  borderRadius: vars.spacing.borderRadius.md,
  outline: 'none',
  transition: `border-color ${vars.animation.duration.normal} ${vars.animation.easing.easeOut}, box-shadow ${vars.animation.duration.normal} ${vars.animation.easing.easeOut}`,
  width: '100%',
  position: 'relative' as const,
  boxShadow: vars.form.fieldInsetShadow,
  backdropFilter: vars.surface.liquidGlass.backdropFilter.light,
  '@media': {
    [reducedMotion]: {
      transition: 'none',
    },
  },
} as const;

export const inputRecipe = recipe({
  base: inputBase,

  variants: {
    inputSize: {
      xs: {
        height: '28px',
        padding: `0 ${vars.spacing.padding.sm}`,
        fontSize: vars.typography.fontSize.xs,
        borderRadius: vars.spacing.borderRadius.sm,
        // 폰 — 16px 미만이면 iOS 가 포커스 때 화면을 확대한다(__tests__/mobileInputZoom.test.ts)
        '@media': { [mobile]: { fontSize: vars.typography.fontSize.body } },
      },
      sm: {
        height: '40px',
        padding: '10px 16px',
        fontSize: vars.typography.fontSize.sm,
        '@media': { [mobile]: { fontSize: vars.typography.fontSize.body } },
      },
      md: {
        height: '48px',
        padding: '14px 20px',
        fontSize: vars.typography.fontSize.base,
        '@media': { [mobile]: { fontSize: vars.typography.fontSize.body } },
      },
      lg: { height: '56px', padding: '18px 24px', fontSize: vars.typography.fontSize.md },
    },
    variant: {
      default: {},
      error: {
        borderColor: vars.bright.status.error,
      },
      success: {
        borderColor: vars.bright.status.success,
      },
    },
    isFocused: {
      true: {
        borderColor: vars.bright.primary.default,
        boxShadow: `${vars.form.fieldInsetShadow}, 0 0 12px ${vars.surface.highlight.medium}`,
      },
    },
    isDisabled: {
      true: {
        opacity: 0.4,
        cursor: 'not-allowed',
        background: vars.surface.card.disabled,
        boxShadow: vars.form.fieldDisabledShadow,
      },
    },
    hasLeftIcon: {
      true: {},
    },
    hasRightIcon: {
      true: {},
    },
    surface: {
      glass: {},
      flat: {
        background: vars.surface.background.secondary,
        border: `1px solid ${vars.surface.border.secondary}`,
        boxShadow: 'none',
        backdropFilter: 'none',
      },
    },
  },

  compoundVariants: [
    {
      // flat 의 포커스는 테두리 색만 — glow 도 inset 도 없다.
      variants: { surface: 'flat', isFocused: true },
      style: {
        borderColor: vars.bright.primary.default,
        boxShadow: 'none',
      },
    },
    { variants: { hasLeftIcon: true, inputSize: 'sm' }, style: { paddingLeft: '44px' } },
    { variants: { hasLeftIcon: true, inputSize: 'md' }, style: { paddingLeft: '52px' } },
    { variants: { hasLeftIcon: true, inputSize: 'lg' }, style: { paddingLeft: '60px' } },
    // xs 는 rightIconPositions 에만 있고 padding 복합 variant 가 빠져 있어 아이콘 아래로 글자가
    // 파고들었다. 아래 3단(h+4) 대신 26px 인 것은 의도다 — right:10px + 아이콘 12px + 여백 4px 이
    // 실수요이고, 조밀한 표(28px 행)에서 32px 을 떼면 글자 폭이 눈에 띄게 줄어든다.
    { variants: { hasRightIcon: true, inputSize: 'xs' }, style: { paddingRight: '26px' } },
    { variants: { hasRightIcon: true, inputSize: 'sm' }, style: { paddingRight: '44px' } },
    { variants: { hasRightIcon: true, inputSize: 'md' }, style: { paddingRight: '52px' } },
    { variants: { hasRightIcon: true, inputSize: 'lg' }, style: { paddingRight: '60px' } },
  ],

  defaultVariants: {
    inputSize: 'md',
    variant: 'default',
    isFocused: false,
    isDisabled: false,
    hasLeftIcon: false,
    hasRightIcon: false,
    surface: 'glass',
  },
});

// Hover on wrapper -> style the input (only when not disabled)
globalStyle(`${wrapper}:hover input:not(:disabled)`, {
  borderColor: vars.surface.border.accent,
});

// Placeholder styles
globalStyle(`${wrapper} input::placeholder`, {
  color: vars.typography.textColor.tertiary,
  opacity: 1,
  transition: `opacity ${vars.animation.duration.normal} ${vars.animation.easing.easeOut}, transform ${vars.animation.duration.normal} ${vars.animation.easing.easeOut}`,
  '@media': { [reducedMotion]: { transition: 'none' } },
});

globalStyle(`${wrapper} input:focus::placeholder`, {
  opacity: 0.5,
});

globalStyle(`${wrapper} input:focus-visible`, {
  outline: `${vars.focus.ring.width} ${vars.focus.ring.style} ${vars.focus.ring.color}`,
  outlineOffset: vars.focus.ring.offset,
  borderColor: vars.bright.primary.default,
  boxShadow: `${vars.form.fieldInsetShadow}, 0 0 12px ${vars.surface.highlight.medium}`,
});

// flat 은 hover/focus 에서도 glow 를 켜지 않는다 — 속성 셀렉터라 위 글래스 globalStyle 보다
// 구체도가 높아 이긴다(Select flat 이 같은 방식을 쓴다). 포커스 링은 base 를 그대로 공유한다.
globalStyle(`${wrapper}:hover input[data-surface='flat']:not(:disabled)`, {
  borderColor: vars.bright.primary.default,
});

globalStyle(`${wrapper} input[data-surface='flat']:focus-visible`, {
  boxShadow: 'none',
});

// ============================================
// ICON STYLES
// ============================================

export const iconBase = style({
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  color: vars.bright.secondary.default,
  transition: `color ${vars.animation.duration.normal} ${vars.animation.easing.easeOut}`,
  zIndex: vars.elevation.interactive,
});

export const iconFocused = style({
  color: vars.bright.secondary.default,
});

export const leftIconPositions = styleVariants({
  xs: { left: '10px', fontSize: vars.typography.fontSize.sm },
  sm: { left: '14px', fontSize: vars.typography.fontSize.base },
  md: { left: '18px', fontSize: vars.typography.fontSize.md },
  lg: { left: '22px', fontSize: vars.typography.fontSize.lg },
});

export const rightIconPositions = styleVariants({
  xs: { right: '10px', fontSize: vars.typography.fontSize.sm },
  sm: { right: '14px', fontSize: vars.typography.fontSize.base },
  md: { right: '18px', fontSize: vars.typography.fontSize.md },
  lg: { right: '22px', fontSize: vars.typography.fontSize.lg },
});

// ============================================
// ERROR MESSAGE
// ============================================

export const errorMessage = style({
  fontFamily: vars.typography.fontFamily.primary,
  fontSize: vars.typography.fontSize.xs,
  fontWeight: vars.typography.fontWeight.medium,
  color: vars.bright.status.error,
  marginTop: vars.spacing.gap.xs,
  letterSpacing: vars.typography.letterSpacing.wide,
});
